/**
 * Market insights data for all countries
 * This includes salary data, job market trends, and other insights
 */

export type MarketInsight = {
  countryCode: string;
  countryName: string;
  currency: string;
  currencySymbol: string;
  averageSalary: string;
  salaryRange: {
    min: string;
    max: string;
  };
  mostInDemandSkills: string[];
  jobGrowthRate: string;
  topEmployers: string[];
  jobOpenings: string;
  competitionLevel: "Low" | "Medium" | "High" | "Very High";
  bestTimeToApply: string;
  locale: string;
  flag: string;
};

export const MARKET_INSIGHTS: Record<string, MarketInsight> = {
  // Gulf Countries
  JO: {
    countryCode: "JO",
    countryName: "Jordan",
    currency: "JOD",
    currencySymbol: "د.أ",
    averageSalary: "JOD 800-1,500",
    salaryRange: {
      min: "JOD 500",
      max: "JOD 3,000",
    },
    mostInDemandSkills: [
      "Software Development",
      "Data Analysis",
      "Digital Marketing",
      "Arabic-English Translation",
    ],
    jobGrowthRate: "12% annually",
    topEmployers: ["Aramex", "Hikma Pharmaceuticals", "Arab Bank", "Orange Jordan"],
    jobOpenings: "2,500+ monthly",
    competitionLevel: "Medium",
    bestTimeToApply: "Sunday-Tuesday mornings",
    locale: "ar-JO",
    flag: "🇯🇴",
  },
  AE: {
    countryCode: "AE",
    countryName: "United Arab Emirates",
    currency: "AED",
    currencySymbol: "د.إ",
    averageSalary: "AED 8,000-15,000",
    salaryRange: {
      min: "AED 4,000",
      max: "AED 30,000",
    },
    mostInDemandSkills: ["Cloud Computing", "AI/ML", "Blockchain", "Project Management"],
    jobGrowthRate: "15% annually",
    topEmployers: ["Emirates Group", "Etisalat", "ADNOC", "Dubai Holding"],
    jobOpenings: "15,000+ monthly",
    competitionLevel: "High",
    bestTimeToApply: "Sunday-Tuesday mornings",
    locale: "ar-AE",
    flag: "🇦🇪",
  },
  SA: {
    countryCode: "SA",
    countryName: "Saudi Arabia",
    currency: "SAR",
    currencySymbol: "ر.س",
    averageSalary: "SAR 7,000-12,000",
    salaryRange: {
      min: "SAR 4,000",
      max: "SAR 25,000",
    },
    mostInDemandSkills: ["Oil & Gas", "Construction", "Healthcare", "IT Services"],
    jobGrowthRate: "18% annually",
    topEmployers: ["Saudi Aramco", "SABIC", "STC", "NEOM"],
    jobOpenings: "20,000+ monthly",
    competitionLevel: "Medium",
    bestTimeToApply: "Sunday-Tuesday mornings",
    locale: "ar-SA",
    flag: "🇸🇦",
  },
  KW: {
    countryCode: "KW",
    countryName: "Kuwait",
    currency: "KWD",
    currencySymbol: "د.ك",
    averageSalary: "KWD 800-1,500",
    salaryRange: {
      min: "KWD 500",
      max: "KWD 3,000",
    },
    mostInDemandSkills: ["Finance", "Engineering", "Healthcare", "Education"],
    jobGrowthRate: "10% annually",
    topEmployers: ["Kuwait Petroleum", "NBK", "Zain", "Agility"],
    jobOpenings: "5,000+ monthly",
    competitionLevel: "Medium",
    bestTimeToApply: "Sunday-Tuesday mornings",
    locale: "ar-KW",
    flag: "🇰🇼",
  },
  QA: {
    countryCode: "QA",
    countryName: "Qatar",
    currency: "QAR",
    currencySymbol: "ر.ق",
    averageSalary: "QAR 8,000-14,000",
    salaryRange: {
      min: "QAR 5,000",
      max: "QAR 25,000",
    },
    mostInDemandSkills: ["Construction", "Hospitality", "Aviation", "Sports Management"],
    jobGrowthRate: "14% annually",
    topEmployers: ["Qatar Airways", "QatarEnergy", "Ooredoo", "Qatar Foundation"],
    jobOpenings: "8,000+ monthly",
    competitionLevel: "High",
    bestTimeToApply: "Sunday-Tuesday mornings",
    locale: "ar-QA",
    flag: "🇶🇦",
  },
  BH: {
    countryCode: "BH",
    countryName: "Bahrain",
    currency: "BHD",
    currencySymbol: "د.ب",
    averageSalary: "BHD 600-1,200",
    salaryRange: {
      min: "BHD 400",
      max: "BHD 2,500",
    },
    mostInDemandSkills: ["Banking", "FinTech", "Tourism", "Real Estate"],
    jobGrowthRate: "11% annually",
    topEmployers: ["Batelco", "Gulf Air", "Ahli United Bank", "ALBA"],
    jobOpenings: "3,000+ monthly",
    competitionLevel: "Medium",
    bestTimeToApply: "Sunday-Tuesday mornings",
    locale: "ar-BH",
    flag: "🇧🇭",
  },
  OM: {
    countryCode: "OM",
    countryName: "Oman",
    currency: "OMR",
    currencySymbol: "ر.ع",
    averageSalary: "OMR 600-1,100",
    salaryRange: {
      min: "OMR 400",
      max: "OMR 2,000",
    },
    mostInDemandSkills: ["Oil & Gas", "Tourism", "Logistics", "Mining"],
    jobGrowthRate: "9% annually",
    topEmployers: ["PDO", "Omantel", "Bank Muscat", "OQ"],
    jobOpenings: "4,000+ monthly",
    competitionLevel: "Low",
    bestTimeToApply: "Sunday-Tuesday mornings",
    locale: "ar-OM",
    flag: "🇴🇲",
  },

  // North America
  US: {
    countryCode: "US",
    countryName: "United States",
    currency: "USD",
    currencySymbol: "$",
    averageSalary: "$60,000-$90,000",
    salaryRange: {
      min: "$40,000",
      max: "$150,000",
    },
    mostInDemandSkills: [
      "Software Engineering",
      "Data Science",
      "Cloud Architecture",
      "Product Management",
    ],
    jobGrowthRate: "8% annually",
    topEmployers: ["Google", "Amazon", "Microsoft", "Apple"],
    jobOpenings: "500,000+ monthly",
    competitionLevel: "Very High",
    bestTimeToApply: "Tuesday-Thursday mornings",
    locale: "en-US",
    flag: "🇺🇸",
  },
  CA: {
    countryCode: "CA",
    countryName: "Canada",
    currency: "CAD",
    currencySymbol: "C$",
    averageSalary: "C$55,000-C$85,000",
    salaryRange: {
      min: "C$40,000",
      max: "C$130,000",
    },
    mostInDemandSkills: ["Healthcare", "Engineering", "IT Services", "Finance"],
    jobGrowthRate: "7% annually",
    topEmployers: ["Shopify", "RBC", "TD Bank", "Bombardier"],
    jobOpenings: "100,000+ monthly",
    competitionLevel: "High",
    bestTimeToApply: "Tuesday-Thursday mornings",
    locale: "en-CA",
    flag: "🇨🇦",
  },

  // Europe
  GB: {
    countryCode: "GB",
    countryName: "United Kingdom",
    currency: "GBP",
    currencySymbol: "£",
    averageSalary: "£35,000-£55,000",
    salaryRange: {
      min: "£25,000",
      max: "£90,000",
    },
    mostInDemandSkills: ["FinTech", "Healthcare", "Digital Marketing", "Cybersecurity"],
    jobGrowthRate: "6% annually",
    topEmployers: ["HSBC", "BP", "Unilever", "GSK"],
    jobOpenings: "150,000+ monthly",
    competitionLevel: "High",
    bestTimeToApply: "Tuesday-Thursday mornings",
    locale: "en-GB",
    flag: "🇬🇧",
  },
  DE: {
    countryCode: "DE",
    countryName: "Germany",
    currency: "EUR",
    currencySymbol: "€",
    averageSalary: "€45,000-€70,000",
    salaryRange: {
      min: "€35,000",
      max: "€100,000",
    },
    mostInDemandSkills: ["Engineering", "Manufacturing", "Automotive", "SAP"],
    jobGrowthRate: "5% annually",
    topEmployers: ["BMW", "Siemens", "SAP", "Volkswagen"],
    jobOpenings: "120,000+ monthly",
    competitionLevel: "Medium",
    bestTimeToApply: "Tuesday-Thursday mornings",
    locale: "de-DE",
    flag: "🇩🇪",
  },
  FR: {
    countryCode: "FR",
    countryName: "France",
    currency: "EUR",
    currencySymbol: "€",
    averageSalary: "€35,000-€55,000",
    salaryRange: {
      min: "€28,000",
      max: "€80,000",
    },
    mostInDemandSkills: ["Luxury Goods", "Aerospace", "Tourism", "Fashion"],
    jobGrowthRate: "4% annually",
    topEmployers: ["Total", "L'Oréal", "Airbus", "BNP Paribas"],
    jobOpenings: "80,000+ monthly",
    competitionLevel: "High",
    bestTimeToApply: "Tuesday-Thursday mornings",
    locale: "fr-FR",
    flag: "🇫🇷",
  },

  // Asia Pacific
  IN: {
    countryCode: "IN",
    countryName: "India",
    currency: "INR",
    currencySymbol: "₹",
    averageSalary: "₹6,00,000-₹12,00,000",
    salaryRange: {
      min: "₹3,00,000",
      max: "₹25,00,000",
    },
    mostInDemandSkills: [
      "IT Services",
      "Software Development",
      "Data Analytics",
      "Digital Transformation",
    ],
    jobGrowthRate: "12% annually",
    topEmployers: ["TCS", "Infosys", "Wipro", "Reliance"],
    jobOpenings: "200,000+ monthly",
    competitionLevel: "Very High",
    bestTimeToApply: "Monday-Wednesday mornings",
    locale: "hi-IN",
    flag: "🇮🇳",
  },
  CN: {
    countryCode: "CN",
    countryName: "China",
    currency: "CNY",
    currencySymbol: "¥",
    averageSalary: "¥100,000-¥200,000",
    salaryRange: {
      min: "¥60,000",
      max: "¥400,000",
    },
    mostInDemandSkills: ["E-commerce", "AI/ML", "Manufacturing", "Mobile Development"],
    jobGrowthRate: "10% annually",
    topEmployers: ["Alibaba", "Tencent", "Huawei", "ByteDance"],
    jobOpenings: "1,000,000+ monthly",
    competitionLevel: "Very High",
    bestTimeToApply: "Monday-Wednesday mornings",
    locale: "zh-CN",
    flag: "🇨🇳",
  },
  JP: {
    countryCode: "JP",
    countryName: "Japan",
    currency: "JPY",
    currencySymbol: "¥",
    averageSalary: "¥4,500,000-¥7,000,000",
    salaryRange: {
      min: "¥3,000,000",
      max: "¥12,000,000",
    },
    mostInDemandSkills: ["Robotics", "Gaming", "Automotive", "Electronics"],
    jobGrowthRate: "3% annually",
    topEmployers: ["Toyota", "Sony", "Nintendo", "SoftBank"],
    jobOpenings: "150,000+ monthly",
    competitionLevel: "Medium",
    bestTimeToApply: "Monday-Wednesday mornings",
    locale: "ja-JP",
    flag: "🇯🇵",
  },
  AU: {
    countryCode: "AU",
    countryName: "Australia",
    currency: "AUD",
    currencySymbol: "A$",
    averageSalary: "A$70,000-A$100,000",
    salaryRange: {
      min: "A$50,000",
      max: "A$150,000",
    },
    mostInDemandSkills: ["Mining", "Healthcare", "Education", "Construction"],
    jobGrowthRate: "6% annually",
    topEmployers: ["BHP", "Commonwealth Bank", "Telstra", "Woolworths"],
    jobOpenings: "50,000+ monthly",
    competitionLevel: "Medium",
    bestTimeToApply: "Tuesday-Thursday mornings",
    locale: "en-AU",
    flag: "🇦🇺",
  },

  // Latin America
  BR: {
    countryCode: "BR",
    countryName: "Brazil",
    currency: "BRL",
    currencySymbol: "R$",
    averageSalary: "R$40,000-R$80,000",
    salaryRange: {
      min: "R$25,000",
      max: "R$150,000",
    },
    mostInDemandSkills: ["Agribusiness", "FinTech", "E-commerce", "Renewable Energy"],
    jobGrowthRate: "8% annually",
    topEmployers: ["Petrobras", "Itaú", "Vale", "Ambev"],
    jobOpenings: "100,000+ monthly",
    competitionLevel: "High",
    bestTimeToApply: "Tuesday-Thursday mornings",
    locale: "pt-BR",
    flag: "🇧🇷",
  },
  MX: {
    countryCode: "MX",
    countryName: "Mexico",
    currency: "MXN",
    currencySymbol: "$",
    averageSalary: "$180,000-$360,000",
    salaryRange: {
      min: "$120,000",
      max: "$600,000",
    },
    mostInDemandSkills: ["Manufacturing", "Tourism", "Automotive", "Nearshoring Services"],
    jobGrowthRate: "7% annually",
    topEmployers: ["FEMSA", "América Móvil", "Cemex", "Grupo Bimbo"],
    jobOpenings: "80,000+ monthly",
    competitionLevel: "Medium",
    bestTimeToApply: "Tuesday-Thursday mornings",
    locale: "es-MX",
    flag: "🇲🇽",
  },

  // Africa
  ZA: {
    countryCode: "ZA",
    countryName: "South Africa",
    currency: "ZAR",
    currencySymbol: "R",
    averageSalary: "R300,000-R600,000",
    salaryRange: {
      min: "R180,000",
      max: "R1,200,000",
    },
    mostInDemandSkills: ["Mining", "Finance", "Tourism", "Renewable Energy"],
    jobGrowthRate: "5% annually",
    topEmployers: ["Naspers", "Standard Bank", "MTN", "Sasol"],
    jobOpenings: "40,000+ monthly",
    competitionLevel: "High",
    bestTimeToApply: "Tuesday-Thursday mornings",
    locale: "en-ZA",
    flag: "🇿🇦",
  },
  NG: {
    countryCode: "NG",
    countryName: "Nigeria",
    currency: "NGN",
    currencySymbol: "₦",
    averageSalary: "₦2,400,000-₦4,800,000",
    salaryRange: {
      min: "₦1,200,000",
      max: "₦10,000,000",
    },
    mostInDemandSkills: ["FinTech", "Oil & Gas", "Agriculture", "Entertainment"],
    jobGrowthRate: "10% annually",
    topEmployers: ["Dangote", "MTN Nigeria", "Access Bank", "Nigerian Breweries"],
    jobOpenings: "50,000+ monthly",
    competitionLevel: "Very High",
    bestTimeToApply: "Monday-Wednesday mornings",
    locale: "en-NG",
    flag: "🇳🇬",
  },
  EG: {
    countryCode: "EG",
    countryName: "Egypt",
    currency: "EGP",
    currencySymbol: "E£",
    averageSalary: "E£120,000-E£240,000",
    salaryRange: {
      min: "E£60,000",
      max: "E£480,000",
    },
    mostInDemandSkills: ["Tourism", "Construction", "Textiles", "IT Outsourcing"],
    jobGrowthRate: "9% annually",
    topEmployers: ["Orascom", "CIB", "Telecom Egypt", "EFG Hermes"],
    jobOpenings: "60,000+ monthly",
    competitionLevel: "High",
    bestTimeToApply: "Sunday-Tuesday mornings",
    locale: "ar-EG",
    flag: "🇪🇬",
  },
};

/**
 * Get market insights for a specific country
 */
export function getMarketInsights(countryCode: string): MarketInsight | null {
  return MARKET_INSIGHTS[countryCode.toUpperCase()] || null;
}

/**
 * Get default market insights (US as fallback)
 */
export function getDefaultMarketInsights(): MarketInsight {
  return MARKET_INSIGHTS.US;
}
